import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createWatch, deleteWatch, getWatchlist, updateWatch } from "./StockWatchService";
import { downloadCsv, parseCsv, toCsv } from "./StockCsvUtils";

export default function StockWatchList({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const importRef = useRef(null);
  const [form, setForm] = useState({
    symbol: "",
    stockName: "",
    currentBuyingPrice: "",
    targetBuyPrice: "",
    targetSellPrice: "",
    remarks: "",
  });

  useEffect(() => {
    load();
  }, [refreshKey]);

  const load = async () => {
    const data = await getWatchlist();
    setItems(data || []);
  };

  const getAlert = (w) => {
    const current = Number(w.currentBuyingPrice || 0);
    const targetBuy = w.targetBuyPrice != null ? Number(w.targetBuyPrice) : null;
    const targetSell = w.targetSellPrice != null ? Number(w.targetSellPrice) : null;
    if (targetSell != null && current >= targetSell) {
      return { label: "SELL ALERT", type: "sell" };
    }
    if (targetBuy != null && current <= targetBuy) {
      return { label: "BUY ALERT", type: "buy" };
    }
    return { label: "OK", type: "ok" };
  };

  const alertCounts = useMemo(() => {
    let buy = 0;
    let sell = 0;
    items.forEach((w) => {
      const alert = getAlert(w);
      if (alert.type === "buy") buy += 1;
      if (alert.type === "sell") sell += 1;
    });
    return { buy, sell };
  }, [items]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete watchlist item?")) return;
    await deleteWatch(id);
    load();
  };

  const startEdit = (w) => {
    setEditId(w.id);
    setForm({
      symbol: w.symbol || "",
      stockName: w.stockName || "",
      currentBuyingPrice: w.currentBuyingPrice ?? "",
      targetBuyPrice: w.targetBuyPrice ?? "",
      targetSellPrice: w.targetSellPrice ?? "",
      remarks: w.remarks || "",
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm({
      symbol: "",
      stockName: "",
      currentBuyingPrice: "",
      targetBuyPrice: "",
      targetSellPrice: "",
      remarks: "",
    });
  };

  const saveEdit = async () => {
    if (!editId || !form.symbol) return;
    await updateWatch(editId, {
      symbol: form.symbol,
      stockName: form.stockName || null,
      currentBuyingPrice: form.currentBuyingPrice === "" ? null : Number(form.currentBuyingPrice),
      targetBuyPrice: form.targetBuyPrice === "" ? null : Number(form.targetBuyPrice),
      targetSellPrice: form.targetSellPrice === "" ? null : Number(form.targetSellPrice),
      remarks: form.remarks || "",
    });
    cancelEdit();
    load();
  };

  const handleExport = () => {
    const headers = [
      "symbol",
      "stockName",
      "currentBuyingPrice",
      "targetBuyPrice",
      "targetSellPrice",
      "remarks",
    ];
    const rows = items.map((w) => ({
      symbol: w.symbol,
      stockName: w.stockName,
      currentBuyingPrice: w.currentBuyingPrice,
      targetBuyPrice: w.targetBuyPrice,
      targetSellPrice: w.targetSellPrice,
      remarks: w.remarks,
    }));
    downloadCsv("stock-watchlist.csv", toCsv(headers, rows));
  };

  const getField = (row, keys) => {
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== "") return row[key];
    }
    return "";
  };

  const handleImport = async (file) => {
    if (!file) return;
    setIsImporting(true);
    try {
      const text = await file.text();
      const { rows } = parseCsv(text);
      let created = 0;
      let skipped = 0;
      for (const row of rows) {
        const symbol = getField(row, ["symbol", "ticker"]);
        if (!symbol) {
          skipped += 1;
          continue;
        }
        await createWatch({
          symbol,
          stockName: getField(row, ["stockname", "name"]) || null,
          currentBuyingPrice: getField(row, ["currentbuyingprice", "currentprice"])
            ? Number(getField(row, ["currentbuyingprice", "currentprice"]))
            : null,
          targetBuyPrice: getField(row, ["targetbuyprice", "targetbuy"])
            ? Number(getField(row, ["targetbuyprice", "targetbuy"]))
            : null,
          targetSellPrice: getField(row, ["targetsellprice", "targetsell"])
            ? Number(getField(row, ["targetsellprice", "targetsell"]))
            : null,
          remarks: getField(row, ["remarks", "note", "notes"]) || "",
        });
        created += 1;
      }
      load();
      window.alert(`Import complete. Created: ${created}, Skipped: ${skipped}.`);
    } finally {
      setIsImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  return (
    <div className="card">
      <div className="stocks-toolbar">
        <div className="stocks-summary">
          <span className="stocks-badge">Buy Alerts: {alertCounts.buy}</span>
          <span className="stocks-badge">Sell Alerts: {alertCounts.sell}</span>
          <span className="stocks-badge">Total: {items.length}</span>
        </div>
        <div className="row-actions">
          <button className="btn btn-secondary" onClick={handleExport}>
            Export CSV
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e) => handleImport(e.target.files[0])}
          />
          <button className="btn btn-primary" onClick={() => importRef.current && importRef.current.click()}>
            {isImporting ? "Importing..." : "Import CSV"}
          </button>
        </div>
      </div>
      <div className="data-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Name</th>
              <th>Current Buy</th>
              <th>Target Buy</th>
              <th>Target Sell</th>
              <th>Remarks</th>
              <th>Alert</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <Fragment key={w.id}>
                <tr>
                  <td>{w.symbol}</td>
                  <td>{w.stockName}</td>
                  <td>{w.currentBuyingPrice}</td>
                  <td>{w.targetBuyPrice}</td>
                  <td>{w.targetSellPrice}</td>
                  <td>{w.remarks}</td>
                  <td>
                    {(() => {
                      const alert = getAlert(w);
                      return (
                        <span className={`stocks-badge stocks-alert-${alert.type}`}>
                          {alert.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    <button className="btn btn-secondary" onClick={() => startEdit(w)}>
                      Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDelete(w.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
                {editId === w.id && (
                  <tr>
                    <td colSpan="8">
                      <div className="stack-form stocks-inline-form">
                        <input
                          value={form.symbol}
                          onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                          placeholder="Symbol"
                          required
                        />
                        <input
                          value={form.stockName}
                          onChange={(e) => setForm({ ...form, stockName: e.target.value })}
                          placeholder="Stock name"
                        />
                        <input
                          type="number"
                          value={form.currentBuyingPrice}
                          onChange={(e) => setForm({ ...form, currentBuyingPrice: e.target.value })}
                          placeholder="Current buying price"
                        />
                        <input
                          type="number"
                          value={form.targetBuyPrice}
                          onChange={(e) => setForm({ ...form, targetBuyPrice: e.target.value })}
                          placeholder="Target buying price"
                        />
                        <input
                          type="number"
                          value={form.targetSellPrice}
                          onChange={(e) => setForm({ ...form, targetSellPrice: e.target.value })}
                          placeholder="Target selling price"
                        />
                        <input
                          value={form.remarks}
                          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                          placeholder="Remarks"
                        />

                        <div className="row-actions">
                          <button className="btn btn-primary" onClick={saveEdit}>
                            Save
                          </button>
                          <button className="btn btn-secondary" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan="8">No watchlist items found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
